import {MessageProps, MessageType} from '@digivox/design-system-smartspace';
import {AttachmentExtension, AttachmentType, ConferenceChatMessage,} from '@/types/conferenceChat.types';
import {Participant} from 'livekit-client';

// ============================================================================
// Constantes para detecção de tipo de mídia
// ============================================================================

const IMAGE_EXTENSIONS = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'svg'];
// webm removido da lista de vídeo pois pode ser áudio também - tratado separadamente
const VIDEO_EXTENSIONS = ['mp4', 'avi', 'mov', 'mkv', 'flv', 'wmv'];
const AUDIO_EXTENSIONS = ['mp3', 'wav', 'ogg', 'oga', 'aac', 'flac', 'm4a', 'wma'];
// Extensões ambíguas que precisam de análise do nome do arquivo
const AMBIGUOUS_EXTENSIONS = ['webm'];
const DOCUMENT_EXTENSIONS = ['pdf', 'doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx', 'txt', 'csv', 'xml'];

// Padrões no nome do arquivo que indicam áudio
const AUDIO_FILENAME_PATTERNS = ['audio', 'voice', 'recording', 'sound', 'gravacao', 'voz'];

// Domínios conhecidos de upload de arquivos
const UPLOAD_DOMAINS = [
  'squad-upload-labchat.digivox.com.br',
  'upload.digivox.com.br',
  'labchat.digivox.com.br',
];

// ============================================================================
// Funções de detecção de tipo de mídia por URL
// ============================================================================

/**
 * Verifica se uma string é uma URL válida
 */
export const isValidUrl = (text: string): boolean => {
  try {
    new URL(text);
    return true;
  } catch {
    return false;
  }
};

/**
 * Verifica se a URL é de um domínio de upload conhecido
 */
export const isUploadUrl = (url: string): boolean => {
  try {
    const urlObj = new URL(url);
    return UPLOAD_DOMAINS.some(domain => urlObj.hostname.includes(domain));
  } catch {
    return false;
  }
};

/**
 * Extrai o nome do arquivo de uma URL
 */
export const getFileNameFromUrl = (url: string): string => {
  try {
    const urlObj = new URL(url);
    const pathname = urlObj.pathname;
    return pathname.split('/').pop() || '';
  } catch {
    return '';
  }
};

/**
 * Extrai a extensão de uma URL
 */
export const getExtensionFromUrl = (url: string): string => {
  const fileName = getFileNameFromUrl(url);
  return fileName.split('.').pop()?.toLowerCase() || '';
};

/**
 * Verifica se o nome do arquivo indica que é um áudio
 * Ex: "audio.webm", "voice_message.webm", "recording.webm"
 */
export const isAudioFileName = (fileName: string): boolean => {
  const lowerFileName = fileName.toLowerCase();
  return AUDIO_FILENAME_PATTERNS.some(pattern => lowerFileName.includes(pattern));
};

/**
 * Detecta o tipo de mídia baseado na extensão e nome do arquivo
 */
export const detectMediaTypeFromExtension = (extension: string, fileName: string = ''): MessageType => {
  const ext = extension.toLowerCase();
  
  if (IMAGE_EXTENSIONS.includes(ext)) {
    return MessageType.IMAGE;
  }
  
  // Para extensões ambíguas (webm), analisar o nome do arquivo
  if (AMBIGUOUS_EXTENSIONS.includes(ext)) {
    // Se o nome do arquivo indica áudio, retorna AUDIO
    if (isAudioFileName(fileName)) {
      return MessageType.AUDIO;
    }
    // Caso contrário, assume vídeo (padrão para webm)
    return MessageType.VIDEO;
  }
  
  if (VIDEO_EXTENSIONS.includes(ext)) {
    return MessageType.VIDEO;
  }
  
  if (AUDIO_EXTENSIONS.includes(ext)) {
    return MessageType.AUDIO;
  }
  
  if (DOCUMENT_EXTENSIONS.includes(ext)) {
    return MessageType.FILE;
  }
  
  return MessageType.TEXT;
};

/**
 * Detecta o tipo de mídia de uma URL
 * Analisa a extensão do arquivo e o nome para determinar o tipo
 */
export const detectMediaTypeFromUrl = (url: string): MessageType => {
  if (!isValidUrl(url)) {
    return MessageType.TEXT;
  }

  const fileName = getFileNameFromUrl(url);
  const extension = getExtensionFromUrl(url);
  
  // Se tiver extensão reconhecida, usa ela (passando o nome do arquivo para análise)
  if (extension) {
    const typeFromExtension = detectMediaTypeFromExtension(extension, fileName);
    if (typeFromExtension !== MessageType.TEXT) {
      return typeFromExtension;
    }
  }

  // Se for URL de upload sem extensão reconhecida, trata como arquivo
  if (isUploadUrl(url)) {
    return MessageType.FILE;
  }

  // URLs genéricas são tratadas como link
  if (url.startsWith('http://') || url.startsWith('https://')) {
    return MessageType.LINK;
  }

  return MessageType.TEXT;
};

/**
 * Verifica se o conteúdo da mensagem é uma URL de mídia
 */
export const isMediaUrl = (content: string): boolean => {
  if (!isValidUrl(content)) return false;
  
  const fileName = getFileNameFromUrl(content);
  const extension = getExtensionFromUrl(content);
  
  // Verificar extensões de mídia (incluindo ambíguas)
  return (
    IMAGE_EXTENSIONS.includes(extension) ||
    VIDEO_EXTENSIONS.includes(extension) ||
    AUDIO_EXTENSIONS.includes(extension) ||
    AMBIGUOUS_EXTENSIONS.includes(extension)
  );
};

// ============================================================================
// Funções de formatação de data/hora
// ============================================================================

/**
 * Formata uma data para exibição de hora no formato HH:mm
 */
export const formatMessageTime = (dateString: string | undefined): string => {
  const date = dateString ? new Date(dateString) : new Date();
  return date.toLocaleTimeString('pt-BR', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
};

/**
 * Formata uma data para exibição de cabeçalho de dia
 */
export const formatDateHeader = (dateString: string): string => {
  const date = new Date(dateString);
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);

  if (isSameDay(date, today)) {
    return 'Hoje';
  }
  if (isSameDay(date, yesterday)) {
    return 'Ontem';
  }

  return date.toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: 'long',
    year: 'numeric',
  });
};

/**
 * Verifica se duas datas são do mesmo dia
 */
export const isSameDay = (date1: Date, date2: Date): boolean => {
  return (
    date1.getFullYear() === date2.getFullYear() &&
    date1.getMonth() === date2.getMonth() &&
    date1.getDate() === date2.getDate()
  );
};

/**
 * Agrupa mensagens por data
 */
export const groupMessagesByDate = (
  messages: ConferenceChatMessage[]
): Record<string, ConferenceChatMessage[]> => {
  return messages.reduce(
    (groups, message) => {
      const date = new Date(message.sentAt || message.time || new Date());
      const dateKey = date.toISOString().split('T')[0]; // YYYY-MM-DD

      if (!groups[dateKey]) {
        groups[dateKey] = [];
      }
      groups[dateKey].push(message);

      return groups;
    },
    {} as Record<string, ConferenceChatMessage[]>
  );
};

// ============================================================================
// Funções de mapeamento de mensagem
// ============================================================================

/**
 * Mapeia o tipo de conteúdo do chat para o MessageType do design system.
 * Eventos preservam o tipo e o conteúdo fornecidos pela API. Arquivos
 * preservam o subtipo de mídia quando ele puder ser identificado.
 */
export const getMessageType = (message: ConferenceChatMessage): MessageType => {
  if (message.contentType === 'event') {
    return MessageType.EVENT;
  }

  if (message.contentType === 'file') {
    if (message.isImageMessage) return MessageType.IMAGE;
    if (message.isVideoMessage) return MessageType.VIDEO;
    if (message.isAudioMessage) return MessageType.AUDIO;

    const fileUrl = message.fileUrl || message.message || message.content;
    const detectedType = fileUrl ? detectMediaTypeFromUrl(fileUrl) : MessageType.TEXT;
    return detectedType === MessageType.IMAGE ||
      detectedType === MessageType.VIDEO ||
      detectedType === MessageType.AUDIO
      ? detectedType
      : MessageType.FILE;
  }

  return MessageType.TEXT;
};

/**
 * Cria os metadados de anexo exigidos pelas variantes FILE, IMAGE, VIDEO e
 * AUDIO do design system. Sem esse objeto o componente tenta acessar campos
 * internos do anexo durante a renderização.
 */
const buildAttachment = (
  message: ConferenceChatMessage,
  type: MessageType,
): MessageProps['attachment'] | undefined => {
  if (
    message.contentType !== 'file' ||
    ![MessageType.FILE, MessageType.IMAGE, MessageType.VIDEO, MessageType.AUDIO].includes(type)
  ) {
    return undefined;
  }

  const url = message.fileUrl || message.message || message.content || '';
  const name = message.fileName || getFileNameFromUrl(url) || 'Arquivo';
  const extension = getFileExtension(name) || getExtensionFromUrl(url);
  const attachmentType =
    type === MessageType.IMAGE
      ? AttachmentType.IMAGE
      : type === MessageType.VIDEO
        ? AttachmentType.VIDEO
        : type === MessageType.AUDIO
          ? AttachmentType.AUDIO
          : AttachmentType.DOC;

  return {
    name,
    type: attachmentType,
    extension: convertToAttachmentExtension(extension),
    size: message.fileSize !== undefined ? String(message.fileSize) : '',
    url,
  };
};

/**
 * Extrai o UUID do campo identity do LiveKit
 * O identity pode vir no formato "uuid:suffix" (ex: "03dfff25-0000-8151-c67f-f5476aad7e71:be270034")
 * Esta função retorna apenas o UUID, ignorando o sufixo após ":"
 * @param identity - Identity do participante do LiveKit
 * @returns UUID extraído ou o identity original se não houver sufixo
 */
export const extractUuidFromIdentity = (identity: string): string => {
  if (!identity) return '';

  const separatorIndex = identity.search(/[#:]/);
  return (separatorIndex > 0 ? identity.substring(0, separatorIndex) : identity).trim();
};

/**
 * Resolve o nome do remetente a partir da lista de participantes do LiveKit
 * e/ou cache de participantes conhecidos
 * @param fromUserId - ID do usuário que enviou a mensagem (UUID)
 * @param userName - Nome do usuário (fallback se disponível na mensagem)
 * @param participants - Lista de participantes do LiveKit
 * @param knownParticipants - Cache de participantes conhecidos (id -> nome)
 * @returns Nome do participante ou fallback para ID
 */
export const resolveSenderName = (
  fromUserId: string,
  userName: string | undefined,
  participants?: Participant[],
  knownParticipants?: Record<string, string>,
): string => {
  const normalizedFromUserId = extractUuidFromIdentity(fromUserId).toLowerCase();

  // 1. Primeiro tenta buscar no cache de participantes conhecidos.
  if (knownParticipants) {
    const cachedName = Object.entries(knownParticipants).find(([participantId]) =>
      extractUuidFromIdentity(participantId).toLowerCase() === normalizedFromUserId,
    )?.[1];
    if (cachedName) return cachedName;
  }

  // 2. Se não encontrou no cache, busca na lista do LiveKit.
  if (participants && participants.length > 0) {
    const participant = participants.find((p) => {
      const participantUuid = extractUuidFromIdentity(p.identity).toLowerCase();
      return participantUuid === normalizedFromUserId;
    });
    if (participant?.name) {
      return participant.name;
    }
  }

  // 3. Fallback para userName da mensagem ou ID
  return userName || fromUserId;
};

/**
 * Resolve the actual text content of a message.
 * For reply/historic messages the `message` field may contain a raw JSON payload;
 * this helper extracts the human-readable text from it.
 */
const resolveMessageContent = (message: ConferenceChatMessage): string => {
  if (message.contentType === 'event') {
    return message.content ?? message.message ?? '';
  }

  const structuredContent = message.content ?? '';
  const rawContent = message.fileUrl || structuredContent || message.message || '';
  const normalizedContent = rawContent.trimStart();

  /*
   * Payloads de resposta podem conter tags de menção dentro do JSON.
   * Por isso, o JSON deve ser interpretado antes da detecção de menções.
   */
  if (normalizedContent.startsWith('{')) {
    try {
      const parsed: unknown = JSON.parse(normalizedContent);

      if (
        parsed !== null &&
        typeof parsed === 'object' &&
        'message' in parsed &&
        typeof parsed.message === 'string'
      ) {
        return parsed.message;
      }
    } catch {
      // Não é um JSON válido; continua com o conteúdo original.
    }
  }

  /*
   * O conteúdo estruturado só é priorizado quando contém ao menos uma marcação
   * de menção, evitando substituir mensagens comuns por conteúdo vazio.
   */
  if (/<mention\b[^>]*>.*?<\/mention>/i.test(structuredContent)) {
    return structuredContent;
  }

  return rawContent;
};

/**
 * Indica se a mensagem possui algum conteúdo efetivamente renderizável.
 * Mensagens vazias, contendo apenas espaços ou payloads de resposta com
 * `message` vazio não devem ser enviadas ao MessageList.
 */
export const hasRenderableMessageContent = (
  message: ConferenceChatMessage,
): boolean => resolveMessageContent(message).trim().length > 0;

/**
 * Builds the `replyMsg` prop expected by the DS MessageList component.
 * Tries `message.reply` first, then falls back to parsing the JSON payload
 * stored in `message.content` / `message.message`.
 */
const buildReplyMsg = (
  message: ConferenceChatMessage,
  participants?: Participant[],
  knownParticipants?: Record<string, string>,
): MessageProps | undefined => {
  // 1. Structured reply field (non-historic messages)
  if (message.reply) {
    return {
      type: MessageType.TEXT,
      content: parseMentionBody(message.reply.message || ''),
      isOutgoing: false,
      sender: resolveSenderName(
        message.reply.userId || message.reply.to || '',
        message.reply.userName,
        participants,
        knownParticipants,
      ),
      time: formatMessageTime(message.reply.timestamp),
    };
  }

  // 2. Fallback: parse from JSON payload (historic messages or messages not yet parsed)
  const source = message.content || message.message || '';
  if (source.startsWith('{')) {
    try {
      const parsed = JSON.parse(source);
      if (parsed?.reply_msg !== undefined || parsed?.reply_msg_id) {
        return {
          type: MessageType.TEXT,
          content: parseMentionBody(
            typeof parsed.reply_msg === 'string' ? parsed.reply_msg : '',
          ),
          isOutgoing: false,
          sender: resolveSenderName(
            parsed.reply_user_id || parsed.reply_to || '',
            parsed.reply_user_name,
            participants,
            knownParticipants,
          ),
          time: formatMessageTime(parsed.reply_timestamp),
        };
      }
    } catch {
      // Not parseable — no reply info available
    }
  }

  return undefined;
};

/**
 * Converte ConferenceChatMessage para o formato MessageProps do design system
 * @param message - Mensagem do chat
 * @param participants - Lista opcional de participantes do LiveKit para resolver nomes
 * @param knownParticipants - Cache de participantes conhecidos (id -> nome)
 */
export const mapToMessageProps = (
  message: ConferenceChatMessage,
  participants?: Participant[],
  knownParticipants?: Record<string, string>,
  currentUserId?: string | null,
): MessageProps => {
  const replyMsg = buildReplyMsg(message, participants, knownParticipants);
  const type = getMessageType(message);
  const attachment = buildAttachment(message, type);

  return {
    id: message.idXmpp || message.id,
    type,
    content: resolveMessageContent(message),
    currentUserId: currentUserId ?? undefined,
    isOutgoing: message.isOwn,
    sender: resolveSenderName(message.fromUser, message.userName, participants, knownParticipants),
    time: formatMessageTime(message.sentAt || message.time),
    ...(attachment ? { attachment } : {}),
    ...(replyMsg ? { replyMsg } : {}),
  };
};

// ============================================================================
// Funções de conversão de tipos de anexo
// ============================================================================

/**
 * Converte MIME type para AttachmentType
 */
export const convertMimeToAttachmentType = (mimeType: string): AttachmentType => {
  const lowerType = mimeType?.toLowerCase() || '';

  if (lowerType.startsWith('image/')) {
    return AttachmentType.IMAGE;
  }
  if (lowerType.startsWith('video/')) {
    return AttachmentType.VIDEO;
  }
  if (lowerType.startsWith('audio/')) {
    return AttachmentType.AUDIO;
  }
  if (lowerType.startsWith('application/') || lowerType.startsWith('text/')) {
    return AttachmentType.DOC;
  }

  // Fallback para tipos simples
  switch (lowerType) {
    case 'image':
      return AttachmentType.IMAGE;
    case 'video':
      return AttachmentType.VIDEO;
    case 'audio':
      return AttachmentType.AUDIO;
    default:
      return AttachmentType.DOC;
  }
};

/**
 * Converte extensão de arquivo para AttachmentExtension
 */
export const convertToAttachmentExtension = (extension: string): AttachmentExtension => {
  const ext = extension.toLowerCase().replace(/^\./, '');

  const extensionMap: Record<string, AttachmentExtension> = {
    // Imagens
    jpeg: AttachmentExtension.JPG,
    jpg: AttachmentExtension.JPG,
    png: AttachmentExtension.PNG,
    gif: AttachmentExtension.GIF,
    webp: AttachmentExtension.WEBP,
    // Vídeos
    mp4: AttachmentExtension.MP4,
    webm: AttachmentExtension.WEBM,
    // Áudios
    mp3: AttachmentExtension.MP3,
    wav: AttachmentExtension.WAV,
    ogg: AttachmentExtension.OGG,
    oga: AttachmentExtension.OGG,
    wave: AttachmentExtension.WAVE,
    // Documentos
    pdf: AttachmentExtension.PDF,
    doc: AttachmentExtension.DOC,
    docx: AttachmentExtension.DOCX,
    txt: AttachmentExtension.TXT,
    xlsx: AttachmentExtension.XLSX,
    xls: AttachmentExtension.XLS,
    xml: AttachmentExtension.XML,
    csv: AttachmentExtension.CSV,
    html: AttachmentExtension.HTML,
    xhtml: AttachmentExtension.XHTML,
  };

  return extensionMap[ext] || AttachmentExtension.TXT;
};

/**
 * Extrai a extensão de um nome de arquivo
 */
export const getFileExtension = (fileName: string): string => {
  const parts = fileName.split('.');
  return parts.length > 1 ? parts[parts.length - 1].toLowerCase() : '';
};

// ============================================================================
// Funções auxiliares de mensagem
// ============================================================================

/**
 * Verifica se uma mensagem é do tipo de mídia (imagem, vídeo, áudio)
 */
export const isMediaMessage = (message: ConferenceChatMessage): boolean => {
  // Verificar flags primeiro
  if (message.isImageMessage || message.isVideoMessage || message.isAudioMessage) {
    return true;
  }
  
  // Detectar pela URL
  const content = message.fileUrl || message.message || '';
  if (isValidUrl(content)) {
    const type = detectMediaTypeFromUrl(content);
    return type === MessageType.IMAGE || type === MessageType.VIDEO || type === MessageType.AUDIO;
  }
  
  return false;
};

/**
 * Verifica se uma mensagem tem anexo
 */
export const hasAttachment = (message: ConferenceChatMessage): boolean => {
  return !!(message.isFileMessage || isMediaMessage(message));
};

/**
 * Gera um ID único para mensagem
 */
export const generateMessageId = (): string => {
  return `msg_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
};

/**
 * Verifica se duas mensagens são do mesmo remetente
 */
export const isSameSender = (
  message1: ConferenceChatMessage | undefined,
  message2: ConferenceChatMessage | undefined,
): boolean => {
  if (!message1 || !message2) return false;
  return message1.fromUser === message2.fromUser;
};

/**
 * Determina se deve mostrar o nome do usuário na mensagem
 * (Mostra apenas quando o remetente é diferente do anterior)
 */
export const shouldShowUserName = (
  message: ConferenceChatMessage,
  previousMessage: ConferenceChatMessage | undefined
): boolean => {
  if (message.isOwn) return false;
  return !isSameSender(message, previousMessage);
};

/**
 * Remove somente a marcação estrutural das menções e preserva seu rótulo para
 * previews e outros contextos que precisam de texto simples.
 */
export const parseMentionBody = (body: string): string =>
  body.replace(
    /<mention\b[^>]*>(.*?)<\/mention>/gi,
    (_match, label: string) => `${label}`
  );

/*
 * Escapa caracteres especiais dos nomes antes de utilizá-los em uma
 * expressão regular construída dinamicamente.
 */
const escapeRegExp = (value: string): string =>
  value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/**
 * Reconstrói marcações estruturadas para clientes que enviam o texto visível
 * e os metadados de menção separadamente.
 */
export const buildMentionContent = (
  text: string,
  mentions: Array<{
    id: string;
    label: string;
    isAll?: boolean;
  }>,
  mentionAuthorId?: string,
): string => {
  if (!text || mentions.length === 0 || /<mention\b[^>]*>.*?<\/mention>/i.test(text)) {
    return text;
  }

  /*
   * Rótulos maiores são processados primeiro para evitar substituições parciais
   * quando dois participantes possuem nomes semelhantes.
   */
  const orderedMentions = [...mentions].sort(
    (left, right) => right.label.length - left.label.length,
  );

  return orderedMentions.reduce((content, mention) => {
    const label = mention.label.trim();
    if (!label) return content;

    const mentionId = mention.isAll && mentionAuthorId ? mentionAuthorId : mention.id;

    /*
     * A menção só termina no fim do texto ou antes de espaço ou pontuação,
     * impedindo correspondências dentro de outras palavras.
     */
    const pattern = new RegExp(`@${escapeRegExp(label)}(?=$|\\s|[.,!?;:])`, 'gi');

    return content.replace(pattern, `<mention id="${mentionId}">${label}</mention>`);
  }, text);
};